import React, { useState, useCallback } from 'react'
import { Field } from 'react-final-form'
import styled from 'styled-components'
import { OnChange } from 'react-final-form-listeners'
import { IntlProvider } from 'react-intl'

import { useRequest } from '../hooks/request'
import { VARIANT_SUCCESS } from '../constants/variants'
import { confirmPassword } from '../requests/confirmPassword'
import { ROLES } from '../constants/roles'
import { login } from '../requests/login'
import { loginRedirect } from '../utils/loginRedirect'
import { resetPassword } from '../requests/resetPassword'

import { PageTitle } from './common/PageTitle'
import { Form } from './common/Form'
import { Button } from './common/Button'
import { Menu } from './common/Typography'
import { ConfirmModal } from './common/ConfirmModal'
import { PasswordField } from './fields/PasswordField'
import { CheckboxField } from './fields/CheckboxField'
import { RgpdText } from './RgpdText'
import { useParams } from 'react-router-dom'

const validate = ({ password, passwordConfirm }) => {
  if (!password) {
    return { password: 'validation.required' }
  }
  if (password !== passwordConfirm) {
    return {
      passwordConfirm: 'validation.mustMatch',
    }
  }
}

const validateRgpd = (value) => {
  if (!value) {
    return 'validation.required'
  }
}

const createConfirmPassword = ({ tokenName, req }) => () => {
  const tokenParam = useParams()
  const [isBasicUser, setIsBasicUser] = useState(false)
  const [pw, setPw] = useState()
  const [isOpen, setIsOpen] = useState(false)
  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const closeAndUncheck = useCallback(() => {
    window.setFormValue('rgpdAgreement', false)
    setIsOpen(false)
  }, [])
  const confirmPasswordRequest = useRequest({
    request: ({ params }) => req({ params: { [tokenName]: tokenParam[tokenName], password: params.password } }),
    handler: (p) => p.then((user) => {
      if (user.role === ROLES.BASIC_USER) {
        return setIsBasicUser(true)
      }

      return login({ params: { password: pw, email: user.email } }).then((token) => loginRedirect({ token }))
    }).catch((e) => {
      if (e.errorCode === 10000) {
        return {
          passwordConfirm: 'confirmPasswordRequest.10000',
        }
      }

      if (e.errorCode === 10009) {
        return {
          passwordConfirm: 'confirmPasswordRequest.10009',
        }
      }

      if (e.errorCode === 14000) {
        return {
          passwordConfirm: 'confirmPasswordRequest.14000',
        }
      }

      return {}
    }),
  })
  const messages = {
    password: 'Mot de passe',
    cancel: 'Annuler',
    confirm: 'Confirmer',
    'confirmPasswordRequest.10000': 'Votre mot de passe doit contenir au moins: 1 minuscule, 1 majuscule, 1 chiffre, 1 caractère spécial (#?!@$%^&*-_+=.(){}[\\];:<>,\\/), et 8 caractères (12 pour un admin)',
    'confirmPasswordRequest.10009': '12 caractères pour un admin',
    'confirmPasswordRequest.14000': 'Jeton invalide. Demandez de recevoir un autre email de confirmation.',
    'validation.enum': 'Merci de choisir parmis les valeurs proposées',
    'validation.mustMatch': 'Le mot de passe et la confirmation doivent correspondre',
    'validation.required': 'Ce champ est obligatoire',
    passwordConfirm: 'Confirmation du mot de passe',
    passwordConfirmPlaceholder: 'Répétez-le...',
    passwordPlaceholder: '...',
    choosePassword: 'Choisissez votre mot de passe',
    rgpdAgreement: 'Je reconnais avoir pris connaissance des mentions légales de l\'application Les alpps. J\'autorise l\'armée de Terre à traiter mes données personnelles pour les seules finalités décrites dans les mentions légales *',
    rgpdConfirm: 'Mentions légales',
    changePasswordSuccess: 'Vous pouvez maintenant vous connecter sur l\'application mobile.',
  }

  return (
    <IntlProvider locale="fr" messages={messages}>
      <Wrapper>
        <PageTitle tradId="choosePassword" />
        {
          confirmPasswordRequest.state.success && isBasicUser ? (
            <Menu tradId="changePasswordSuccess" />
          ) : (

            <Form
              validate={validate}
              onSubmit={confirmPasswordRequest.fetch}
              mutators={{
                setValue: ([field, value], state, { changeValue }) => {
                  changeValue(state, field, () => value)
                },
              }}
            >
              {({ handleSubmit, form }) => {
                if (!window.setFormValue) window.setFormValue = form.mutators.setValue

                return (
                  <>
                    <Field
                      required
                      component={PasswordField}
                      name="password"
                      label="password"
                      placeholder="passwordPlaceholder"
                    />
                    <Field
                      required
                      component={PasswordField}
                      name="passwordConfirm"
                      label="passwordConfirm"
                      placeholder="passwordConfirmPlaceholder"
                    />
                    <OnChange name="password">
                      {(value) => {
                        setPw(value)
                      }}
                    </OnChange>
                    <Field
                      component={CheckboxField}
                      name="rgpdAgreement"
                      label="rgpdAgreement"
                      type="checkbox"
                      validate={validateRgpd}
                    />
                    <OnChange name="rgpdAgreement">
                      {(value, previous) => {
                        if (!previous && value === true) {
                          open()
                        }
                      }}
                    </OnChange>
                    {isOpen && (
                      <ConfirmModal
                        onConfirm={close}
                        confirmDisabled={false}
                        onCloseRequest={closeAndUncheck}
                      >
                        <RgpdText />
                      </ConfirmModal>
                    )}
                    <Button
                      fullWidth
                      onClick={handleSubmit}
                      loading={form.getState().submitting}
                      tradId="confirm"
                      filled
                      variant={VARIANT_SUCCESS}
                    />
                  </>
                )
              }}
            </Form>
          )
        }
      </Wrapper>
    </IntlProvider>
  )
}


export const ConfirmPassword = createConfirmPassword({ tokenName: 'confirmToken', req: confirmPassword })
export const ResetPassword = createConfirmPassword({ tokenName: 'resetToken', req: resetPassword })
const Wrapper = styled.div`
  display: flex;
  height: 100vh;
  justify-content: center;
  flex-direction: column;
  @media (max-width: 500px) {
    margin: 20px;
  }
  @media (min-width: 500px) {
    margin: auto;
    width: 400px;
  }
`
